import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef } from '@angular/core';
import { MunilyPanelData, MunilyPanelDescription } from './panel.types';

@Component({
  selector: 'common-panel',
  templateUrl: './panel.component.html',
  styleUrls: ['./panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PanelComponent implements OnChanges {
  @Input() data: MunilyPanelData = { title: '' };
  /** Optional slot for header right side (e.g. text button). When present, it replaces subtitle + chevron. */
  @Input() headerRightTemplate?: TemplateRef<unknown>;
  /** Optional context for `headerRightTemplate` (available as `$implicit`). */
  @Input() headerRightContext?: unknown;
  /** Optional footer slot to render custom content (e.g. bullet list, alert). */
  @Input() footerTemplate?: TemplateRef<unknown>;
  /** Optional context for `footerTemplate` (available as `$implicit`). */
  @Input() footerContext?: unknown;

  @Output() select = new EventEmitter<void>();

  public buttonClasses: string[] = [];
  public titleClasses: string[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.syncClasses();
    }
  }

  constructor() {
    this.syncClasses();
  }

  get title(): string {
    return this.data?.title ?? '';
  }

  get subtitle(): string {
    return this.data?.subtitle ?? '';
  }

  get description(): string {
    return this.data?.description ?? '';
  }

  get showDescription(): boolean {
    return this.data?.showDescription ?? false;
  }

  get showSubtitle(): boolean {
    return this.data?.showSubtitle ?? false;
  }

  get showChevron(): boolean {
    return this.data?.showChevron ?? false;
  }

  get isTitleMuted(): boolean {
    return this.data?.titleTone === 'muted';
  }

  get isBorderless(): boolean {
    return this.data?.bordered === false;
  }

  get showFooter(): boolean {
    return this.data?.showFooter ?? false;
  }

  get visibleDescriptions(): MunilyPanelDescription[] {
    return (this.data?.descriptions ?? []).filter(d => d.visible !== false);
  }

  get hasFooterContent(): boolean {
    return (this.showFooter && this.visibleDescriptions.length > 0) || !!this.footerTemplate;
  }

  onClick(): void {
    this.select.emit();
  }

  private syncClasses(): void {
    this.buttonClasses = this.isBorderless ? ['munily-panel--borderless'] : [];
    this.titleClasses = this.isTitleMuted ? ['munily-panel__title--muted'] : [];
  }
}
